// @flow
//
// `@uniflowed/core/random`: randomness that two renders can agree on.
//
// `Math.random()` and `crypto.randomUUID()` differ by construction, which is
// the whole point of them and the reason neither can appear in a render that
// happens twice. A shuffled list on the server is a different shuffle in the
// browser, React compares the markup, and the reader sees the server's order
// snap to the client's on hydration.
//
// React answers half of this with `useId`, which is deliberately *not* random:
// it is derived from the component's position in the tree, so both renders
// produce the same string. That works for an id and for nothing else, because
// a position in a tree is not a number anyone can shuffle with.
//
// So the other half is a seeded stream. One seed is decided once — on the
// server, or on the client for a page nobody prerendered — travels to the other
// side as a short string, and both sides replay the same numbers from it. The
// application gets randomness; hydration gets determinism; the two are the same
// object.
//
// # Why `fork`, and not one shared stream
//
// A single stream makes every consumer depend on the order the others drew in.
// That order is the render order, which is stable in a synchronous render and
// is *not* stable once a tree suspends: a boundary that resolves late on the
// server and early in the browser reorders the draws, and every component after
// it gets different numbers on the two sides. The bug that produces is the
// worst kind — it is correct in development, correct in a test, and wrong on a
// slow connection.
//
// `fork(label)` removes the question. A child stream is derived from the
// parent's seed and the label, so what it produces depends on its name and on
// nothing that happened before it. Two components that fork `"featured"` and
// `"sidebar"` get independent streams in whatever order they render, and the
// same streams the next time.
//
// # Why this generator
//
// sfc32, seeded through a 128-bit string hash. It is four `>>> 0` words of
// integer arithmetic, which is what matters here: every host that runs uf has
// to produce byte-identical numbers from the same seed, and integer operations
// on 32-bit words are the only arithmetic JavaScript specifies exactly. A
// generator built on floating-point multiplication would drift between engines,
// and drift is a hydration mismatch that only happens on somebody else's
// browser.
//
// Not for anything that has to be unguessable. The seed is in the markup by
// design — that is what makes both sides agree — so a stream derived from it is
// public. Session tokens, password salts and anything else an attacker must not
// predict come from `crypto`, on a server, and never from here.

/** A stream of numbers that any host can replay from the same seed. */
export type Random = {
  /** The next number in `[0, 1)`. */
  readonly next: () => number,
  /** The next integer in `[0, bound)`. Zero when `bound` is not positive. */
  readonly integer: (bound: number) => number,
  /**
   * An independent stream named by `label`.
   *
   * Independent of *when* it is asked for, which is the property the module
   * documentation is about: forking twice with the same label yields two
   * streams that produce the same numbers.
   */
  readonly fork: (label: string) => Random,
};

/**
 * A 128-bit hash of `seed`, as the four words sfc32 starts from.
 *
 * cyrb128. The mixing matters more than it looks like it should: seeds in
 * practice are adjacent — `"uf-1"`, `"uf-2"`, a counter, a request id — and a
 * weaker hash gives adjacent seeds streams that start with visibly similar
 * numbers, which is a shuffle that is not a shuffle.
 */
function words(seed: string): [number, number, number, number] {
  let a = 1779033703;
  let b = 3144134277;
  let c = 1013904242;
  let d = 2773480762;
  for (let at = 0; at < seed.length; at += 1) {
    const code = seed.charCodeAt(at);
    a = b ^ Math.imul(a ^ code, 597399067);
    b = c ^ Math.imul(b ^ code, 2869860233);
    c = d ^ Math.imul(c ^ code, 951274213);
    d = a ^ Math.imul(d ^ code, 2716044179);
  }
  return [
    (Math.imul(b ^ (a >>> 18), 597399067) ^ Math.imul(c ^ (d >>> 22), 2869860233)) >>> 0,
    (Math.imul(c ^ (b >>> 19), 951274213) ^ Math.imul(d ^ (a >>> 21), 2716044179)) >>> 0,
    (Math.imul(d ^ (c >>> 17), 2716044179) ^ Math.imul(a ^ (b >>> 20), 597399067)) >>> 0,
    (Math.imul(a ^ (d >>> 16), 2869860233) ^ Math.imul(b ^ (c >>> 23), 951274213)) >>> 0,
  ];
}

/**
 * A stream that replays exactly, on every host, from `seed`.
 *
 * A number seed is stringified rather than used as state directly, so
 * `seededRandom(7)` and `seededRandom("7")` are the same stream. They arrive at
 * the same place through JSON anyway — one round trip through the markup turns
 * a number into a number and a string into a string, and a caller who wrote one
 * and read the other should not get two different pages.
 */
export function seededRandom(seed: string | number): Random {
  const text = String(seed);
  let [a, b, c, d] = words(text);

  const next = (): number => {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    // Divided by 2^32 rather than multiplied by its reciprocal: the reciprocal
    // is not exactly representable, and the difference is a value that can
    // round to exactly 1 on some engines. `next()` promising `[0, 1)` and
    // returning 1 is an out-of-bounds index in the one call in ten million
    // nobody reproduces.
    return (t >>> 0) / 4294967296;
  };

  return {
    next,
    integer: (bound: number) => (bound > 0 ? Math.floor(next() * bound) : 0),
    // From the seed rather than from the live state, which is what makes a
    // fork independent of everything drawn before it.
    fork: (label: string) => seededRandom(`${text} ${label}`),
  };
}

/**
 * A seed from the host, for the one render that has to decide.
 *
 * Base 36 and eight characters: short enough to sit in the markup without
 * being noticed, and wide enough that two pages rendered in the same
 * millisecond do not collide. Deliberately `Math.random()` — this is the one
 * call site where non-determinism is the requirement rather than the bug, and
 * hiding it behind `crypto` would suggest the value is a secret.
 */
export function hostSeed(): string {
  return Math.floor(Math.random() * 2821109907456)
    .toString(36)
    .padStart(8, "0");
}

/** The stream installed for this process, if any. See `@uniflowed/core/clock`. */
let installed: Random | null = null;

/**
 * The stream every uf package reads.
 *
 * Unseeded until something installs one, and unseeded means seeded from the
 * host: a page nobody prerendered has no other side to agree with, and refusing
 * to produce a number until a seed is installed would make the common case the
 * one that needs configuring.
 */
export function currentRandom(): Random {
  if (installed == null) {
    installed = seededRandom(hostSeed());
  }
  return installed;
}

/**
 * Install `random`, and return the call that puts back whatever was there.
 *
 * The same contract as `setClock`, and for the same reason: a caller that
 * restores what it assumed was there rather than what it found leaves its own
 * stream installed for everything that runs afterwards.
 */
export function setRandom(random: Random | null): () => void {
  const previous = installed;
  installed = random;
  return () => {
    installed = previous;
  };
}

/**
 * `items`, in an order that depends only on `random`.
 *
 * Fisher-Yates over a copy, because a shuffle that mutates its argument is a
 * shuffle that changes a prop, and React compares props. The draw is taken
 * before the swap on every step, so the number of draws is a function of the
 * length alone — a stream is only replayable if both sides take the same
 * number of numbers out of it.
 */
export function shuffled<T>(items: $ReadOnlyArray<T>, random: Random): Array<T> {
  const out = items.slice();
  for (let at = out.length - 1; at > 0; at -= 1) {
    const swap = random.integer(at + 1);
    const held = out[at];
    out[at] = out[swap];
    out[swap] = held;
  }
  return out;
}
